import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'react-feather';
import type { SnackbarProps, SnackbarPosition, SnackbarVariant } from './types';
import { useSnackbar } from './useSnackbar';

// ============================================================================
// Position Styles
// ============================================================================

const positionStyles: Record<SnackbarPosition, string> = {
  'bottom-center': 'fixed bottom-6 left-1/2 z-[10000]',
  'bottom-left': 'fixed bottom-6 left-6 z-[10000]',
  'bottom-right': 'fixed bottom-6 right-6 z-[10000]',
  'top-center': 'fixed top-6 left-1/2 z-[10000]',
  'top-left': 'fixed top-6 left-6 z-[10000]',
  'top-right': 'fixed top-6 right-6 z-[10000]',
};

// ============================================================================
// Variant Styles
// ============================================================================

const variantStyles: Record<
  SnackbarVariant,
  { container: string; text: string; action: string }
> = {
  default: {
    container: 'bg-neutral-20',
    text: 'text-neutral-90',
    action: 'text-primary-color-80 hover:bg-primary-color-80/8',
  },
  success: {
    container: 'bg-success-20',
    text: 'text-success-90',
    action: 'text-success-80 hover:bg-success-80/8',
  },
  error: {
    container: 'bg-error-20',
    text: 'text-error-90',
    action: 'text-error-80 hover:bg-error-80/8',
  },
  warning: {
    container: 'bg-warning-20',
    text: 'text-warning-90',
    action: 'text-warning-80 hover:bg-warning-80/8',
  },
  info: {
    container: 'bg-information-40',
    text: 'text-white',
    action: 'text-white hover:bg-white/8',
  },
};

// ============================================================================
// Snackbar Component
// ============================================================================

export const Snackbar: React.FC<SnackbarProps> = ({
  open,
  message,
  variant = 'default',
  actionLabel,
  onAction,
  showCloseButton = false,
  onClose,
  autoHideDuration = 5000,
  position = 'bottom-center',
  className = '',
}) => {
  const { isVisible, handleClose } = useSnackbar({
    open,
    onClose,
    autoHideDuration,
  });

  if (!isVisible) {
    return null;
  }

  const styles = variantStyles[variant];
  const positionClass = positionStyles[position];

  // Determine animation direction based on position
  const isTopPosition = position.startsWith('top');
  const isCenterPosition = position.endsWith('center');
  const slideAnimation = isTopPosition
    ? isCenterPosition
      ? 'animate-slide-in-from-top-center'
      : 'animate-slide-in-from-top-side'
    : isCenterPosition
      ? 'animate-slide-in-from-bottom-center'
      : 'animate-slide-in-from-bottom-side';

  // Check if message is long (more than ~50 chars or has newlines)
  const isMultiLine = message.length > 50 || message.includes('\n');

  // Determine if action should be on a new line (longer action text)
  const isLongerAction = actionLabel && actionLabel.length > 10;

  const handleActionClick = () => {
    onAction?.();
    handleClose();
  };

  const snackbarContent = (
    <div
      role="alert"
      aria-live="polite"
      className={`
        ${positionClass}
        ${styles.container}
        rounded-[4px]
        shadow-[0_1px_3px_rgba(0,0,0,0.15),0_4px_8px_3px_rgba(0,0,0,0.08)]
        w-[432px]
        ${slideAnimation}
        ${className}
      `}
      style={{
        fontFamily: 'Roboto, sans-serif',
        ...(isCenterPosition && {
          transform: 'translateX(-50%)',
        }),
      }}
    >
      <div
        className={`
          flex
          ${isMultiLine && isLongerAction ? 'flex-col' : 'flex-row items-center'}
          gap-2
          px-4 py-3.5
        `}
      >
        {/* Message */}
        <p
          className={`
            flex-1
            text-bodyMedium
            ${styles.text}
            ${isMultiLine ? '' : 'truncate'}
          `}
        >
          {message}
        </p>

        {/* Actions container */}
        <div
          className={`
            flex
            items-center
            gap-2
            ${isMultiLine && isLongerAction ? 'self-end' : ''}
            shrink-0
          `}
        >
          {/* Action button */}
          {actionLabel && (
            <button
              type="button"
              onClick={handleActionClick}
              className={`
                text-labelLarge
                ${styles.action}
                px-3
                py-[10px]
                rounded-full
                transition-colors
                duration-200
                hover:cursor-pointer
              `}
            >
              {actionLabel}
            </button>
          )}

          {/* Close button */}
          {showCloseButton && (
            <button
              type="button"
              onClick={handleClose}
              aria-label="Zamknij"
              className={`
                rounded-full
                ${styles.text}
                opacity-80
                hover:opacity-100
                hover:bg-white/8
                transition-all
                duration-200
                hover:cursor-pointer
              `}
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(snackbarContent, document.body);
};

export default Snackbar;
